import React, { useEffect, useCallback } from 'react'
import _ from 'lodash'
import { useSpring, animated } from 'react-spring'
import { AutomationTarget } from '../../../types'
import {
  colorVars,
  ColorNames,
  colorLongToShort,
  lightenColor,
  getCssColorFromColorName,
  INHERIT,
} from '../../../utils/colors'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const COLOR_NAMES = _.keys(colorVars).map(colorLongToShort)
const COLOR_OPTIONS = COLOR_NAMES.concat(INHERIT, '') // eslint-disable-line

export interface AnimatedProgressBarProps extends AutomationTarget {
  className?: string
  color: (typeof COLOR_OPTIONS)[number]
  percent: number
  animationSeconds?: number
  height?: number
  rounded?: boolean
  centered?: boolean
  colorNegative?: (typeof COLOR_OPTIONS)[number]
}

const AnimatedProgressBar: React.FC<AnimatedProgressBarProps> = ({
  className,
  elmId,
  color: colorName,
  percent,
  animationSeconds = 0.6,
  height = 10,
  rounded = false,
  centered = false,
  colorNegative = 'error',
}) => {
  const activeColorName = centered && percent < 0 ? colorNegative : colorName
  const backgroundColor = getCssColorFromColorName(
    activeColorName as ColorNames
  )
  const pinstripeColor = lightenColor(backgroundColor, 50)

  const getAnimationStyles = useCallback(
    (targetPercent: number) => {
      if (!centered) {
        // Standard left-to-right mode
        return {
          width: `${Math.max(0, targetPercent)}%`,
          left: '0%',
          transform: 'translateX(0%)',
        }
      }

      // Centered mode - use consistent positioning with transform
      if (targetPercent === 0) {
        // Small sliver in center
        return {
          width: '2px',
          left: '50%',
          transform: 'translateX(-50%)',
        }
      } else if (targetPercent > 0) {
        // Grow from center to right
        return {
          width: `${targetPercent / 2}%`,
          left: '50%',
          transform: 'translateX(0%)',
        }
      } else {
        // Grow from center to left
        return {
          width: `${Math.abs(targetPercent) / 2}%`,
          left: '50%',
          transform: `translateX(-100%)`,
        }
      }
    },
    [centered]
  )

  const [barStyles, setBarStyles] = useSpring(() => ({
    from: getAnimationStyles(0),
    to: getAnimationStyles(percent),
    config: { duration: animationSeconds * 1000 }, // Duration in milliseconds
  }))

  useEffect(() => {
    // eslint-disable-next-line
    setBarStyles({
      to: getAnimationStyles(percent),
      config: { duration: animationSeconds * 1000 },
    })
  }, [percent, animationSeconds, centered, setBarStyles, getAnimationStyles])

  return (
    <div
      className={cx('bar', { rounded }, className)}
      data-elm-id={elmId}
      style={{ height }}
    >
      <animated.div
        style={{
          backgroundColor,
          ...barStyles,
          height: '100%',
          position: 'absolute',
          top: 0,
        }}
        className={cx('bar-inner')}
      >
        {rounded && (
          <div
            style={{
              backgroundColor: pinstripeColor,
              height: '20%',
              left: 0.4 * height,
              right: 0.4 * height,
            }}
            className={cx('pinstripe')}
          />
        )}
      </animated.div>
    </div>
  )
}

export default AnimatedProgressBar
